import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
  Conversation,
  RequestACallDetails,
} from '@stripe-internal/conversations-client';

import {RequestACallCodesEndpointResponseSuccess} from '../../../generated/types';
import {useApi, useEntryPoint, useVisitor} from '../../../core/hooks/appHooks';
import {useConversationEnded} from './useConversationEnded';

type VerificationCodes = {
  agentCode: string;
  userCode: string;
};

type RacStatus = RequestACallCodesEndpointResponseSuccess['racStatus'];

const STATUS_POLLING_FREQUENCY_MS = 5_000; // 5 seconds

export function useRACDetails(conversation: Conversation) {
  const entryPoint = useEntryPoint();
  const api = useApi();
  const visitor = useVisitor();
  const {fetchRequestACallCodes, cancelRequestACall} = api;

  const [verificationCodes, setVerificationCodes] = useState<
    VerificationCodes | undefined
  >();

  const firstFetchRef = useRef<boolean>(false);

  const currTimeout = useRef<NodeJS.Timeout>();
  const racDoneStrikes = useRef<number>(0);
  const isInitialLoading = useRef<boolean>(true);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [racStatus, setRacStatus] = useState<RacStatus>('pending');
  const [isCancelLoading, setIsCancelLoading] = useState<boolean>(false);
  const [callEnded, setCallEnded] = useState<boolean>(false);

  const [racDetails, setRacDetails] = useState<RequestACallDetails | undefined>(
    conversation?.context.requestACallDetails,
  );

  const conversationEnded = useConversationEnded(conversation);

  useEffect(() => {
    if (!conversation) {
      return;
    }

    const sub = conversation
      .contextObservable()
      .subscribe(() => setRacDetails(conversation.context.requestACallDetails));
    return () => sub.unsubscribe();
  }, [conversation]);

  const userName: string = useMemo(
    () => racDetails?.userName || visitor.displayName || '',
    [racDetails, visitor],
  );

  const userPhoneNumber: string = useMemo(
    () => racDetails?.userPhoneNumber || visitor.phoneNumber || '',
    [racDetails, visitor],
  );

  const fetchCodes = useCallback(async () => {
    setLoading(true);

    fetchRequestACallCodes(entryPoint)
      .then((resp) => {
        if (conversationEnded) {
          return;
        }

        if (resp.type === 'success') {
          if (!resp.pending) {
            racDoneStrikes.current += 1;

            if (racDoneStrikes.current >= 2) {
              racDoneStrikes.current = 0;
              setCallEnded(true);
              return;
            }
          } else {
            setVerificationCodes({
              agentCode: resp.agentCode || '',
              userCode: resp.userCode || '',
            });
          }

          isInitialLoading.current = false;
          setRacStatus(resp.racStatus ?? 'calling');

          const nextReloadInMs = resp.nextReload
            ? resp.nextReload * 1000
            : STATUS_POLLING_FREQUENCY_MS;

          const nextTimeout = Math.min(
            nextReloadInMs,
            STATUS_POLLING_FREQUENCY_MS,
          );
          currTimeout.current = setTimeout(fetchCodes, nextTimeout);
        } else {
          throw new Error('Unexpected RAC status response');
        }
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [conversationEnded, entryPoint, fetchRequestACallCodes]);

  useEffect(() => {
    // not using useMountEffect
    // https://trailhead.corp.stripe.com/docs/dashboard/developing-in-dashboard/no-new-use-mount-effect
    if (firstFetchRef.current) {
      return;
    }

    firstFetchRef.current = true;
    fetchCodes();
    return () => {
      return clearTimeout(currTimeout.current);
    };
  });

  useEffect(() => {
    if (conversationEnded) {
      clearTimeout(currTimeout.current);
    }
  }, [conversationEnded]);

  const cancelRac = useCallback(async () => {
    setIsCancelLoading(true);
    cancelRequestACall(entryPoint)
      .then((resp) => {
        if (resp.type === 'success') {
          setCallEnded(true);
          conversation?.setContextValues({
            ...conversation.context,
            requestACallDetails: {
              userName,
              userPhoneNumber,
              callCanceled: true,
            },
          });
        } else if (resp.type === 'conflict' || resp.type === 'not-found') {
          setIsCancelLoading(false);
          setRacStatus('pending');
        } else {
          throw new Error('Unexpected RAC cancel response');
        }
      })
      .catch(() => {
        setIsCancelLoading(false);
        setRacStatus('pending');
      });
  }, [cancelRequestACall, entryPoint, conversation, userName, userPhoneNumber]);

  return {
    userName,
    userPhoneNumber,
    error,
    loading,
    verificationCodes,
    isInitialLoading: isInitialLoading.current,
    racStatus: racStatus ?? 'calling',
    isCancelLoading: isCancelLoading || isInitialLoading.current,
    cancelRac,
    callEnded: conversationEnded || callEnded,
  };
}
